import { useEffect, useState } from "react"
import Typography from "aldoo-ra/Typography"
import LocalizationController from "./localization-controller"
import { usePromise } from "aldoo-ra/usePromise"
import {
  LanguageIcon,
  KeyIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"

// KPI Card component
const KPICard = ({
  title,
  value,
  textColor = "text-admin_text",
  titleColor = "text-admin_text",
  icon,
  progressBar,
}) => {
  return (
    <div className="relative h-[165px] flex flex-col justify-between bg-admin_paper  rounded-xl shadow-sm hover:shadow-md p-6 transition-all duration-200">
      <Typography
        component="h2"
        variant="admin_h4"
        className={twMerge(titleColor, "font-medium leading-none")}
      >
        {title}
      </Typography>

      <div className="flex items-end justify-between">
        <Typography
          component="output"
          variant="admin_h2"
          className={textColor}
        >
          {value}
        </Typography>

        <div className="w-10 md:w-14 h-10 md:h-14 flex items-center justify-center bg-admin_canvas rounded-lg flex-shrink-0">
          {icon}
        </div>
      </div>

      {progressBar && (
        <div className="absolute bottom-6 left-6 w-[60%] md:w-[55%] xl:w-[65%]">
          {progressBar}
        </div>
      )}
    </div>
  )
}

export default function LocalizationOverview() {
  const localizationController = LocalizationController()

  const [languages, setLanguages] = useState([])
  const [statistics, setStatistics] = useState(null)

  const { result: languageContent, execute: refreshStatistics } = usePromise(
    localizationController.getLanguageStatistics
  )

  useEffect(() => {
    if (!languageContent) return
    localizationController.onRefreshStatistics(() => {
      refreshStatistics()
    })
  }, [languageContent])

  useEffect(() => {
    if (!languageContent) return
    setLanguages(languageContent?.languages)

    //find the primary language and set it to the controller
    const primaryLanguage = languageContent?.languages?.find(
      (language) => language.primary
    )
    localizationController.setPrimaryLanguage(primaryLanguage?.code)
    localizationController.setLanguages(languageContent?.languages)

    //extract the stats
    const { progress, keys } = languageContent
    setStatistics({ progress, keys })
  }, [languageContent])

  const kpiCards = [
    {
      id: "languages",
      title: "Languages",
      value: languages?.length || 0,
      textColor: "text-admin_warning",
      icon: <LanguageIcon className="w-7 h-7 text-admin_warning" />,
    },
    {
      id: "keys",
      title: "Keys",
      value: statistics?.keys || 0,
      textColor: "text-admin_primary",
      icon: <KeyIcon className="w-7 h-7 text-admin_primary" />,
    },
    {
      id: "localization",
      title: "Localization",
      value: `${statistics?.progress || 0}%`,
      textColor:
        statistics?.progress === 100 ? "text-admin_success" : "text-admin_progress",
      icon: (
        <ChartBarIcon
          className={`w-7 h-7 ${
            statistics?.progress === 100 ? "text-admin_success" : "text-admin_progress"
          }`}
        />
      ),
      progressBar: (
        <div className="w-full h-1 bg-admin_canvas rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              statistics?.progress === 100 ? "bg-admin_success" : "bg-admin_progress"
            }`}
            style={{ width: `${statistics?.progress || 0}%` }}
          ></div>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 bg-admin_canvas min-h-screen">
      <div className="mb-8">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Localization Overview
        </Typography>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6">
        {kpiCards.map((card) => (
          <KPICard
            key={card.id}
            title={card.title}
            value={card.value}
            bgColor={card.bgColor}
            textColor={card.textColor}
            icon={card.icon}
            progressBar={card.progressBar}
          />
        ))}
      </div>
    </div>
  )
}